import { Truck, Wrench, Camera } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"

interface SpotGridProps {
    total: number
    occupied: number[]
    selected: number | null
    onSelect: (spot: number) => void
    maintenance?: number[]
    rows?: string[]
    columns?: number
    allowMaintenanceClick?: boolean
    /** Set of spot numbers that have at least one photo */
    hasPhotos?: Set<number>
}

export const SpotGrid = ({
    total,
    occupied,
    selected,
    onSelect,
    maintenance = [],
    rows,
    columns = 4,
    allowMaintenanceClick = false,
    hasPhotos,
}: SpotGridProps) => {
    const { t } = useTranslation()
    const occupiedSet = new Set(occupied)
    const maintenanceSet = new Set(maintenance)

    const numCols = columns
    const spots = Array.from({ length: total }, (_, i) => i + 1)
    const numRows = Math.ceil(total / numCols)
    const hasRowLabels = rows !== undefined && rows.length > 0

    return (
        <div className="bg-[#1e293b] rounded-[40px] p-6 shadow-2xl border border-white/5">
            {/* Legend */}
            <div className="flex items-center gap-4 mb-5 flex-wrap">
                {[
                    { cls: "bg-white/10 border-white/20", label: t("guest.reservation.legendAvailable") },
                    { cls: "bg-brand border-[#5ab534]", label: t("guest.reservation.legendSelected") },
                    { cls: "bg-slate-700/40 border-slate-600/30", label: t("guest.reservation.legendOccupied") },
                    ...(maintenance.length > 0
                        ? [{ cls: "bg-slate-200/20 border-slate-300/20 opacity-50", label: t("guest.reservation.legendMaintenance") }]
                        : []),
                ].map(({ cls, label }) => (
                    <span key={label} className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400">
                        <span className={cn("w-3 h-3 rounded border inline-block", cls)} />
                        {label}
                    </span>
                ))}
            </div>

            {/* Scrollable + pinch-zoomable container — browser handles gestures natively */}
            <div
                className="overflow-auto rounded-2xl max-h-[420px] md:max-h-[560px]"
                style={{ WebkitOverflowScrolling: "touch" }}
            >
                {/* Hint bar */}
                <p className="text-[10px] font-bold text-slate-500 text-center mb-3 md:hidden">
                    {t("guest.reservation.zoomHint")}
                </p>

                {/* Fixed-layout grid: always `numCols` columns, min width ensures horizontal scroll on small screens */}
                <div
                    className="inline-block min-w-full"
                    style={{ minWidth: `${numCols * 80 + (hasRowLabels ? 32 : 0) + (numCols - 1) * 10}px` }}
                >
                    {Array.from({ length: numRows }, (_, rowIdx) => {
                        const rowLabel = rows?.[rowIdx]
                        const rowSpots = spots.slice(rowIdx * numCols, (rowIdx + 1) * numCols)

                        return (
                            <div key={rowIdx} className="flex items-stretch gap-2.5 mb-2.5 last:mb-0">
                                {/* Row label (cinema-style) */}
                                {hasRowLabels && (
                                    <div className="flex items-center justify-center w-7 shrink-0">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                                            {rowLabel ?? ""}
                                        </span>
                                    </div>
                                )}

                                {/* Spot cards */}
                                {rowSpots.map((spotNum) => {
                                    const isOccupied = occupiedSet.has(spotNum)
                                    const isMaintenance = maintenanceSet.has(spotNum)
                                    const isSelected = selected === spotNum
                                    const isDisabled = isOccupied || (isMaintenance && !allowMaintenanceClick)

                                    return (
                                        <button
                                            key={spotNum}
                                            type="button"
                                            disabled={isDisabled}
                                            onClick={() => onSelect(spotNum)}
                                            className={cn(
                                                "w-[80px] shrink-0 rounded-2xl p-3 flex flex-col justify-between transition-all duration-200 border-2 min-h-[72px]",
                                                isSelected
                                                    ? "bg-brand border-[#5ab534] scale-105 shadow-lg shadow-brand/30 z-10 relative"
                                                    : isMaintenance
                                                        ? cn("bg-slate-200/20 border-slate-300/20 opacity-40", allowMaintenanceClick ? "cursor-pointer hover:opacity-60" : "cursor-not-allowed")
                                                        : isOccupied
                                                            ? "bg-slate-700/40 border-slate-600/30 cursor-not-allowed"
                                                            : "bg-white/10 border-white/20 hover:bg-white/20 hover:border-white/40 cursor-pointer active:scale-95",
                                            )}
                                        >
                                            {/* Spot number + optional camera icon — top row */}
                                            <div className="flex items-center justify-between">
                                                <span className={cn(
                                                    "text-[10px] font-black uppercase tracking-widest",
                                                    isSelected ? "text-white" : isOccupied || isMaintenance ? "text-slate-500" : "text-slate-300",
                                                )}>
                                                    #{spotNum}
                                                </span>
                                                {hasPhotos?.has(spotNum) && (
                                                    <Camera className={cn(
                                                        "size-3",
                                                        isSelected ? "text-white/80" : "text-slate-400",
                                                    )} />
                                                )}
                                            </div>

                                            {/* Center icon */}
                                            <div className="flex items-center justify-center">
                                                {isOccupied && <Truck className="size-5 text-slate-500" />}
                                                {isMaintenance && <Wrench className="size-5 text-slate-500" />}
                                            </div>

                                        </button>
                                    )
                                })}

                                {/* Empty filler cells so the last row has uniform width */}
                                {rowSpots.length < numCols &&
                                    Array.from({ length: numCols - rowSpots.length }, (_, fi) => (
                                        <div key={`fill-${fi}`} className="w-[80px] shrink-0" />
                                    ))}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
